/**
 * Utility functions for exporting catalog table data with rendered values
 */

import type { ReactNode } from 'react';

/**
 * Column definition type for export processing
 */
export interface ExportColumn {
  title: string;
  field?: string;
  render?: (row: Record<string, unknown>) => ReactNode;
  [key: string]: any;
}

/**
 * Recursively extracts text content from React elements
 * @param element - The React element or primitive value to extract text from
 * @returns The extracted text content as a string
 */
export function extractTextFromReactElement(element: unknown): string {
  // Handle null or undefined
  if (element === null || element === undefined) {
    return '';
  }

  // Handle primitive types
  if (typeof element === 'string' || typeof element === 'number') {
    return String(element);
  }

  // Handle boolean
  if (typeof element === 'boolean') {
    return '';
  }

  // Handle arrays (e.g., multiple children)
  if (Array.isArray(element)) {
    return element.map(extractTextFromReactElement).filter(Boolean).join(' ');
  }

  // Handle React elements
  if (typeof element === 'object') {
    const reactElement = element as Record<string, any>;

    // Check for React element with children
    if (reactElement.props) {
      const { children, title, value, label, text } = reactElement.props;

      // Recursively extract from children
      if (children !== null && children !== undefined) {
        return extractTextFromReactElement(children);
      }

      // Check for common text properties
      if (typeof title === 'string' || typeof title === 'number') {
        return String(title);
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
      }
      if (typeof label === 'string' || typeof label === 'number') {
        return String(label);
      }
      if (typeof text === 'string' || typeof text === 'number') {
        return String(text);
      }
    }

    // Check if it has a meaningful toString
    if (
      reactElement.toString &&
      reactElement.toString !== Object.prototype.toString
    ) {
      const str = reactElement.toString();
      if (str !== '[object Object]') {
        return str;
      }
    }
  }

  // Return empty string instead of [object Object]
  return '';
}

/**
 * Processes table columns and data to include rendered values for export
 * @param cols - Array of column definitions
 * @param datas - Array of data rows
 * @returns Processed columns and data ready for export
 */
export function processColumnsForExport(
  cols: ExportColumn[],
  datas: Array<Record<string, unknown>>,
) {
  // Process columns: ensure all columns have unique field properties and string titles
  const processedCols = cols.map(col => {
    // Extract string title from column (might be React element)
    let titleStr: string;
    if (typeof col.title === 'string') {
      titleStr = col.title;
    } else if (col.title && typeof col.title === 'object') {
      // Try to extract text from React element
      titleStr = extractTextFromReactElement(col.title);

      // If extraction failed, use field name as fallback
      if (!titleStr) {
        titleStr = col.field || 'Unknown';
      }
    } else {
      titleStr = col.field || 'Unknown';
    }

    // Use title as field if no field exists, OR if we need to avoid conflicts
    // For columns with the same field but different titles, use title to make them unique
    const uniqueField = col.field || titleStr;
    const isDuplicateField = cols.filter(c => c.field === col.field).length > 1;

    return {
      ...col,
      title: titleStr, // Ensure title is always a string
      field: isDuplicateField ? titleStr : uniqueField,
    };
  });

  // Process data: The export library passes data as arrays, not objects
  // Convert arrays back to object format with column values
  const processedDatas = datas.map(row => {
    const processedRow: any = {};

    // If row is an array (which it is from the logs), map array indices to column names
    if (Array.isArray(row)) {
      processedCols.forEach((col, colIndex) => {
        // Use the field from the processed column (which already has unique field names)
        const fieldName = col.field;
        const value = row[colIndex];

        // Convert value to string, handling objects
        if (value === null || value === undefined) {
          processedRow[fieldName] = '';
        } else if (typeof value === 'object') {
          // For annotation objects, try to extract meaningful values based on column field
          const titleStr = col.title; // Already a string in processedCols
          const isCloudPlatformCol =
            titleStr.includes('Cloud Platform') ||
            titleStr === 'Cloud Platform';
          const isResourceIdCol =
            titleStr.includes('Resource ID') || titleStr === 'Resource ID';

          if (isCloudPlatformCol) {
            // Check if it's an AWS account by looking for amazonaws.com/account-id
            const isAWS = !!value['amazonaws.com/account-id'];
            processedRow[fieldName] = isAWS ? 'AWS' : 'Azure';
          } else if (isResourceIdCol) {
            // Extract account or subscription ID
            processedRow[fieldName] =
              value['amazonaws.com/account-id'] ||
              value['azure.com/subscription-id'] ||
              '';
          } else {
            // For other objects, try to stringify or leave empty
            processedRow[fieldName] = '';
          }
        } else {
          processedRow[fieldName] = String(value);
        }
      });
    } else {
      // Fallback: handle as object (though we know it's an array from logs)
      processedCols.forEach(col => {
        const fieldName = col.field;
        processedRow[fieldName] = '';
      });
    }

    return processedRow;
  });

  return { processedCols, processedDatas };
}
